'use client';

import { Component, useId, useState, type ReactNode } from 'react';

type AdvisoryErrorBoundaryProps = { children: ReactNode; fallback: ReactNode };

// Mounted only for a failed server preview. A subsequent successful server render
// replaces this leaf entirely; no retries, secondary API call or page-wide fallback.
export class HomepageAdvisoryErrorBoundary extends Component<
  AdvisoryErrorBoundaryProps,
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

type HomepageAdvisoryAnswerProps = {
  children: ReactNode;
  avatar: ReactNode;
  author: ReactNode;
  triggerLabel: string;
  expandLabel: string;
  collapseLabel: string;
  isOpenDefault: boolean;
};

/** Only the disclosure hydrates; author markup and prepared HTML originate on the server. */
export function HomepageAdvisoryAnswer({
  children, avatar, author, triggerLabel, expandLabel, collapseLabel, isOpenDefault,
}: HomepageAdvisoryAnswerProps) {
  const [isOpen, setIsOpen] = useState(isOpenDefault);
  const contentId = useId();

  return (
    <div data-state={isOpen ? 'open' : 'closed'}>
      <div className="ea-card ea-card--default ea-answer mt-2 flex flex-col rounded-[32px] bg-background text-card-foreground transition-all duration-300">
        <div className="ea-card__content ea-answer__content p-1">
          <div className="ea-answer__footer w-full">
            <div id={contentId} hidden={!isOpen} className="ea-answer__collapsible-content ml-0">
              {isOpen && children}
            </div>
            <div className="relative">
              <button
                type="button"
                aria-label={`${triggerLabel} ${isOpen ? collapseLabel : expandLabel}`}
                aria-expanded={isOpen}
                aria-controls={contentId}
                onClick={() => setIsOpen(open => !open)}
                className="absolute inset-0 cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <div className="ea-answer__trigger pointer-events-none relative flex w-full flex-row items-center space-x-4">
                {avatar}
                <div className="ea-answer__footer-row ml-4 flex w-full flex-row items-center justify-between py-3 pr-4 sm:py-0">
                  {author}
                  <div className="flex gap-2 text-primary">
                    <span className="hidden text-sm sm:block">{isOpen ? collapseLabel : expandLabel}</span>
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                      <path d={isOpen ? 'm18 15-6-6-6 6' : 'm6 9 6 6 6-6'} />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
